import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  INTL_LOCALES,
  SOURCE_LANGUAGE,
  isCmsLanguage,
  type CmsLanguage,
} from "./languages";

type DateInput = Date | string | number;

export interface Formatters {
  /** The BCP 47 locale every formatter below uses. */
  locale: string;
  date: (value: DateInput, options?: Intl.DateTimeFormatOptions) => string;
  dateTime: (value: DateInput, options?: Intl.DateTimeFormatOptions) => string;
  time: (value: DateInput, options?: Intl.DateTimeFormatOptions) => string;
  number: (value: number, options?: Intl.NumberFormatOptions) => string;
  currency: (
    value: number,
    currency: string,
    options?: Intl.NumberFormatOptions
  ) => string;
  /** "3 days ago" / "in 2 hours", picking the largest unit that fits. */
  relative: (value: DateInput, now?: DateInput) => string;
}

const toDate = (v: DateInput): Date => (v instanceof Date ? v : new Date(v));

const RELATIVE_UNITS: [Intl.RelativeTimeFormatUnit, number][] = [
  ["year", 365 * 24 * 3600],
  ["month", 30 * 24 * 3600],
  ["week", 7 * 24 * 3600],
  ["day", 24 * 3600],
  ["hour", 3600],
  ["minute", 60],
  ["second", 1],
];

export function makeFormatters(language: CmsLanguage): Formatters {
  const locale = INTL_LOCALES[language];
  const relativeFormat = new Intl.RelativeTimeFormat(locale, {
    numeric: "auto",
  });
  return {
    locale,
    date: (v, o) =>
      toDate(v).toLocaleDateString(locale, o ?? { dateStyle: "medium" }),
    dateTime: (v, o) =>
      toDate(v).toLocaleString(
        locale,
        o ?? { dateStyle: "medium", timeStyle: "short" }
      ),
    time: (v, o) =>
      toDate(v).toLocaleTimeString(locale, o ?? { timeStyle: "short" }),
    number: (v, o) => new Intl.NumberFormat(locale, o).format(v),
    currency: (v, currency, o) =>
      new Intl.NumberFormat(locale, {
        style: "currency",
        currency,
        ...o,
      }).format(v),
    relative: (v, now = Date.now()) => {
      const seconds = (toDate(v).getTime() - toDate(now).getTime()) / 1000;
      const abs = Math.abs(seconds);
      const [unit, size] =
        RELATIVE_UNITS.find(([, s]) => abs >= s) ??
        RELATIVE_UNITS[RELATIVE_UNITS.length - 1];
      return relativeFormat.format(Math.round(seconds / size), unit);
    },
  };
}

/** Locale-aware formatting that follows the CMS language. */
export function useFormat(): Formatters {
  const { i18n } = useTranslation();
  const language = isCmsLanguage(i18n.language)
    ? i18n.language
    : SOURCE_LANGUAGE;
  return useMemo(() => makeFormatters(language), [language]);
}
